import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { EQ_FREQUENCIES, EQ_PRESETS } from '@/lib/dsp';

export type ThemeMode = 'light' | 'dark';
/** Apple Music Sound Check normalizes per track — not user-selectable. */
export type LoudnessMode = 'track';

export type Preferences = {
  theme: ThemeMode;
  loudnessEnabled: boolean;
  loudnessMode: LoudnessMode;
  lateNightEnabled: boolean;
  eqPresetId: string;
  eqGains: number[];
};

/** App prefs localStorage key. */
const STORAGE_KEY = 'graystore';
const LEGACY_STORAGE_KEYS = ['graystore.v1.0', 'tzg-preferences-v1'] as const;

const defaultGains = () => EQ_FREQUENCIES.map(() => 0);

const DEFAULTS: Preferences = {
  theme: 'light',
  loudnessEnabled: true,
  /** Apple Music Sound Check normalizes per track. */
  loudnessMode: 'track',
  lateNightEnabled: false,
  eqPresetId: 'Flat',
  eqGains: defaultGains(),
};

function readStored(): Preferences {
  if (typeof window === 'undefined') return { ...DEFAULTS, eqGains: defaultGains() };
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      for (const legacyKey of LEGACY_STORAGE_KEYS) {
        const legacy = localStorage.getItem(legacyKey);
        if (!legacy) continue;
        raw = legacy;
        try {
          localStorage.setItem(STORAGE_KEY, legacy);
          for (const k of LEGACY_STORAGE_KEYS) localStorage.removeItem(k);
        } catch {
          /* ignore quota / private mode */
        }
        break;
      }
    }
    if (!raw) return { ...DEFAULTS, eqGains: defaultGains() };
    const parsed = JSON.parse(raw) as Partial<Preferences> & {
      compressorEnabled?: boolean;
    };
    const gains = Array.isArray(parsed.eqGains)
      ? EQ_FREQUENCIES.map((_, i) =>
          typeof parsed.eqGains![i] === 'number' ? parsed.eqGains![i]! : 0,
        )
      : defaultGains();
    const lateNightEnabled =
      typeof parsed.lateNightEnabled === 'boolean'
        ? parsed.lateNightEnabled
        : Boolean(parsed.compressorEnabled);
    const presetOk =
      typeof parsed.eqPresetId === 'string' &&
      (parsed.eqPresetId in EQ_PRESETS || parsed.eqPresetId === 'Custom');
    return {
      theme: parsed.theme === 'dark' ? 'dark' : 'light',
      loudnessEnabled: parsed.loudnessEnabled !== false,
      loudnessMode: 'track',
      lateNightEnabled,
      eqPresetId: presetOk ? parsed.eqPresetId! : 'Flat',
      eqGains: gains,
    };
  } catch {
    return { ...DEFAULTS, eqGains: defaultGains() };
  }
}

/** Matches globals.css --background (light 40 10% 98% / dark 40 4% 8%). */
const THEME_CHROME: Record<ThemeMode, string> = {
  light: '#faf9f7',
  dark: '#151413',
};

const CHROME_EDGE_SELECTORS = [
  '.portfolio',
  '.site-header',
  '.mobile-tab-bar',
  '.site-footer',
] as const;

/**
 * Safari (incl. 26 Liquid Glass) tints status + bottom chrome from theme-color
 * and/or opaque page edges. Top samples sticky header reliably; bottom only
 * re-samples after a below-viewport opaque layer exists — the same trick Vaul
 * uses on drawer open (`::after { top:100%; height:200% }`). Keep a permanent
 * body-level sentinel so theme changes work without opening the sheet first.
 * Media-scoped theme-color tags follow the *OS* scheme — collapse to one meta.
 */
function ensureSafariBottomChrome(color: string) {
  let el = document.getElementById('safari-bottom-chrome');
  if (!el) {
    el = document.createElement('div');
    el.id = 'safari-bottom-chrome';
    el.setAttribute('aria-hidden', 'true');
    Object.assign(el.style, {
      position: 'fixed',
      left: '0',
      right: '0',
      // Just below the visual viewport — mirrors Vaul's bottom-drawer ::after.
      top: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '-1',
    });
    document.body.appendChild(el);
  }
  el.style.backgroundColor = color;
}

function syncBrowserChrome(theme: ThemeMode) {
  if (typeof document === 'undefined') return;
  const color = THEME_CHROME[theme];
  const root = document.documentElement;
  root.style.colorScheme = theme;
  root.style.backgroundColor = color;
  root.style.setProperty('--chrome-bg', color);

  const paint = (el: HTMLElement | null) => {
    if (!el) return;
    el.style.backgroundColor = color;
  };
  paint(document.body);
  paint(document.getElementById('root'));
  for (const sel of CHROME_EDGE_SELECTORS) {
    document.querySelectorAll(sel).forEach((node) => paint(node as HTMLElement));
  }
  ensureSafariBottomChrome(color);

  // One theme-color = app theme. Media variants fight in-app light/dark.
  const metas = [
    ...document.querySelectorAll('meta[name="theme-color"]'),
  ] as HTMLMetaElement[];
  if (metas.length) {
    const primary = metas[0]!;
    primary.removeAttribute('media');
    primary.setAttribute('content', color);
    for (let i = 1; i < metas.length; i++) metas[i]!.remove();
  } else {
    const el = document.createElement('meta');
    el.setAttribute('name', 'theme-color');
    el.setAttribute('content', color);
    document.head.appendChild(el);
  }

  let scheme = document.querySelector('meta[name="color-scheme"]');
  if (!scheme) {
    scheme = document.createElement('meta');
    scheme.setAttribute('name', 'color-scheme');
    document.head.appendChild(scheme);
  }
  scheme.setAttribute('content', theme);

  let status = document.querySelector(
    'meta[name="apple-mobile-web-app-status-bar-style"]',
  );
  if (!status) {
    status = document.createElement('meta');
    status.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
    document.head.appendChild(status);
  }
  // Translucent: page edge colour shows through status / home-indicator chrome.
  status.setAttribute('content', 'black-translucent');

  // Light layout pulse so Safari re-reads edge samples (header + sentinel).
  const nudge = () => {
    const header = document.querySelector('.site-header') as HTMLElement | null;
    const target = header || document.body;
    const prev = target.style.transform;
    target.style.transform = 'translateZ(0)';
    void target.offsetHeight;
    target.style.transform = prev || '';
  };
  requestAnimationFrame(() => {
    nudge();
    requestAnimationFrame(nudge);
  });
}

function prefersThemeViewTransition() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  // Mobile Safari samples chrome from VT snapshots → top/bottom lag the body.
  if (window.matchMedia('(max-width: 640px)').matches) return false;
  if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
    return false;
  }
  return typeof document.startViewTransition === 'function';
}

function applyTheme(theme: ThemeMode, { animate = false }: { animate?: boolean } = {}) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const run = () => {
    root.classList.toggle('dark', theme === 'dark');
    root.dataset.theme = theme;
    syncBrowserChrome(theme);
  };
  if (animate && prefersThemeViewTransition()) {
    root.dataset.themeTransit = '1';
    const transition = document.startViewTransition(run);
    void transition.finished.finally(() => {
      delete root.dataset.themeTransit;
      // Re-assert after VT so glass re-samples the settled page, not the snapshot.
      syncBrowserChrome(theme);
    });
    return;
  }
  run();
}

type PreferencesContextValue = Preferences & {
  setTheme: (theme: ThemeMode) => void;
  setLoudnessEnabled: (on: boolean) => void;
  setLateNightEnabled: (on: boolean) => void;
  applyEqPreset: (id: string) => void;
  setEqGain: (index: number, db: number) => void;
  setEqGains: (gains: number[]) => void;
};

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<Preferences>(() => readStored());

  const themeBootstrapped = useRef(false);
  useEffect(() => {
    // First paint: no animation (bootstrapTheme already applied). Later toggles crossfade.
    applyTheme(prefs.theme, { animate: themeBootstrapped.current });
    themeBootstrapped.current = true;
  }, [prefs.theme]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore quota */
    }
  }, [prefs]);

  const setTheme = useCallback((theme: ThemeMode) => {
    setPrefs((p) => ({ ...p, theme }));
  }, []);

  const setLoudnessEnabled = useCallback((loudnessEnabled: boolean) => {
    setPrefs((p) => ({ ...p, loudnessEnabled }));
  }, []);

  const setLateNightEnabled = useCallback((lateNightEnabled: boolean) => {
    setPrefs((p) => ({ ...p, lateNightEnabled }));
  }, []);

  const applyEqPreset = useCallback((id: string) => {
    const preset = EQ_PRESETS[id];
    if (!preset) return;
    setPrefs((p) => ({
      ...p,
      eqPresetId: id,
      eqGains: [...preset],
    }));
  }, []);

  const setEqGain = useCallback((index: number, db: number) => {
    setPrefs((p) => {
      const next = [...p.eqGains];
      next[index] = Math.max(-12, Math.min(12, db));
      return { ...p, eqPresetId: 'Custom', eqGains: next };
    });
  }, []);

  const setEqGains = useCallback((eqGains: number[]) => {
    setPrefs((p) => ({
      ...p,
      eqPresetId: 'Custom',
      eqGains: EQ_FREQUENCIES.map((_, i) =>
        typeof eqGains[i] === 'number' ? Math.max(-12, Math.min(12, eqGains[i]!)) : 0,
      ),
    }));
  }, []);

  const value = useMemo(
    () => ({
      ...prefs,
      loudnessMode: 'track' as const,
      setTheme,
      setLoudnessEnabled,
      setLateNightEnabled,
      applyEqPreset,
      setEqGain,
      setEqGains,
    }),
    [
      prefs,
      setTheme,
      setLoudnessEnabled,
      setLateNightEnabled,
      applyEqPreset,
      setEqGain,
      setEqGains,
    ],
  );

  return (
    <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences outside provider');
  return ctx;
}

/** Apply stored theme before first paint (call from main). */
export function bootstrapTheme() {
  applyTheme(readStored().theme);
}
